import { ColumnType } from "@mondaydotcomorg/api";
import { ALL_MONDAY_COLUMNS } from "@onesolution/constants/monday";

export type BasicColValues = {
	id?: string;
	type: ColumnType;
	display_value?: string;
	text?: string | null;
	from?: Date;
	to?: Date;
	files?: { asset_id: string }[];
	label_style?: {
		color?: string | null;
	} | null;
	email?: string;
	values?: Array<{ label: string; id: string }>;
};

export type BasicItemWithColTitles = {
	name?: string;
	assets?: { id: string; public_url: string; name: string }[];
	column_values?: Array<BasicColValues>;
	parent_item?: BasicItemWithColTitles;
	subitems?: Array<BasicItemWithColTitles>;
};

type BasicColValuesWithoutTitle = Omit<BasicColValues, "column">;

function formatTimeline(value: BasicColValuesWithoutTitle) {
	const [from, to] = [
		"from" as keyof BasicColValuesWithoutTitle,
		"to" as keyof BasicColValuesWithoutTitle,
	].map(
		(key, idx) =>
			value[key] ||
			(value.text
				? new Date(value.text?.split(" - ")[idx] || "").toISOString()
				: null),
	);

	return from && to
		? {
				from,
				to,
			}
		: null;
}

function formatByType(
	value: BasicColValuesWithoutTitle,
	assets: BasicItemWithColTitles["assets"],
) {
	switch (value.type) {
		case ColumnType.BoardRelation:
			return value;
		case ColumnType.Formula:
			return value.display_value === "null" ? null : value.display_value;
		case ColumnType.Mirror:
			return value.display_value;
		case ColumnType.Checkbox:
			return value.text === "v";
		case ColumnType.Email:
			return value.email;
		case ColumnType.File: {
			const relatedAssets = (assets || []).filter((asset) =>
				value.files?.some(({ asset_id }) => asset_id === asset.id),
			);

			return relatedAssets?.map((a) => ({
				url: a.public_url,
				name: a.name,
				id: value.files?.find(({ asset_id }) => asset_id === a.id)?.asset_id,
			}));
		}
		case ColumnType.Date:
			return value.text ? new Date(value.text).toISOString() : null;
		case ColumnType.Timeline:
			return formatTimeline(value);
		case ColumnType.Status:
			return { color: value.label_style?.color, label: value.text };
		case ColumnType.Dropdown:
			return value.values;
		default:
			return value.text || value.display_value;
	}
}

export function basicFormatResult<T>(
	items: Array<BasicItemWithColTitles | null | undefined>,
): Array<T> {
	return ((items || []).filter(Boolean).map((item) => {
		if (!item) return null;
		const { column_values = [], parent_item, subitems = [], ...rest } = item;

		return {
			...rest,
			...column_values.reduce((acc, { id, ...value }) => {
				const col = ALL_MONDAY_COLUMNS.find(([, colId]) => colId === id);

				if (!col) {
					console.error("formating error, col not found", id, item.name);
					return acc;
				}

				return Object.assign(acc, {
					[col[0]]: formatByType(value, rest.assets),
				});
			}, {}),
			...(parent_item
				? { parentItem: basicFormatResult<T>([parent_item])[0] }
				: {}),
			...(subitems ? { subitems: basicFormatResult<T>(subitems) } : {}),
		};
	}) || []) as Array<T>;
}
